import React from 'react';
import { DefaultTheme, withTheme } from 'styled-components';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import {
    SearchDateButton,
    SearchClose,
    SearchCriteriaButton,
    CriteriaText,
    Filter,
    FilterCriteria,
} from '../Button/SearchFilterButton';
import { getCountryText } from '../../../services/helpers/country';
import { getPaymentMethodDisplayName } from '../../../types/order/PaymentMethod';

interface ReadOnlyFilterTagProps {
    filterKey: string;
    values: string[];
    onRemove: (key: string) => void;
    theme: DefaultTheme;
}

const rangeLabels: Record<string, string> = {
    totalAmount: 'Order value',
    activatedAmount: 'Activated amount',
    notActivatedAmount: 'Not activated amount',
    originalOrderAmount: 'Original order value',
    'rows.quantity': 'Row quantity',
    'rows.price': 'Row unit price',
    'rows.totalPrice': 'Row total',
    'rows.vatRate': 'Row VAT %',
};

export const RANGE_FILTER_KEYS = Object.keys(rangeLabels);

const filterLabels: Record<string, string> = {
    ...rangeLabels,
    query: 'Query',
    paymentMethod: 'Payment method',
    currency: 'Currency',
    saleSegment: 'Segment',
    countryCode: 'Country',
    productCode: 'Product code',
    actionReference: 'Action reference',
    'connectedAccounts.invoices.invoiceState': 'Invoice state',
    'connectedAccounts.invoices.paymentStatus.status': 'Payment status',
    'connectedAccounts.invoices.isCanceled': 'Invoice canceled',
    'rows.status': 'Row status',
};

const paymentMethodDisplayNames: Record<string, string> = {
    invoice: 'Invoice',
    account: 'Account',
    installment: 'Installment',
    advanceinvoice: 'AdvanceInvoice',
    card: 'Card',
    banktransfer: 'BankTransfer',
    swish: 'Swish',
    amex: 'Amex',
    dankort: 'Dankort',
    ideal: 'Ideal',
    vipps: 'Vipps',
    mobilepay: 'MobilePay',
    vippsmobilepay: 'VippsMobilePay',
    applepay: 'ApplePay',
    loyalpay: 'LoyalPay',
};

const formatValue = (key: string, value: string): string => {
    switch (key) {
        case 'currency':
            return value.toUpperCase();
        case 'countryCode':
            return getCountryText(value);
        case 'paymentMethod': {
            const displayName = paymentMethodDisplayNames[value.toLowerCase()] ?? value;
            return getPaymentMethodDisplayName(displayName) ?? displayName;
        }
        case 'connectedAccounts.invoices.paymentStatus.status': {
            const paymentStatuses: Record<string, string> = {
                fullypaid: 'Fully paid',
                partiallypaid: 'Partially paid',
                notpaid: 'Not paid',
                paid: 'Paid',
            };
            return paymentStatuses[value.toLowerCase()] ?? value;
        }
        case 'connectedAccounts.invoices.isCanceled':
            return value === 'true' ? 'Yes' : 'No';
        case 'connectedAccounts.invoices.invoiceState': {
            const states: Record<string, string> = {
                onhold: 'On hold',
                preliminary: 'Preliminary',
                canceled: 'Canceled',
                delivered: 'Delivered',
                expired: 'Expired',
                rejected: 'Rejected',
                signing: 'Signing',
                strongcustomerverification: 'Strong customer verification',
            };
            return states[value.toLowerCase()] ?? value;
        }
        case 'rows.status': {
            const rowStates: Record<string, string> = {
                notactivated: 'Not activated',
                activated: 'Activated',
                expired: 'Expired',
                canceled: 'Canceled',
                returned: 'Returned',
            };
            return rowStates[value.toLowerCase()] ?? value;
        }
        default:
            return value;
    }
};

const comparisonSymbols: Record<string, string> = { '>=': '≥', '>': '>', '<=': '≤', '<': '<' };

const parseBound = (value: string) => {
    const match = /^(>=|<=|>|<)(.+)$/.exec(value);
    return match ? { comparison: match[1], number: match[2] } : undefined;
};

const formatRange = (values: string[]): string => {
    const bounds = values.map(parseBound);
    const [lower, upper] = bounds;

    if (lower?.comparison === '>=' && upper?.comparison === '<=')
        return lower.number === upper.number ? lower.number : `${lower.number} – ${upper.number}`;

    return bounds
        .map((bound, i) => (bound ? `${comparisonSymbols[bound.comparison]} ${bound.number}` : values[i]))
        .join(', ');
};

const formatValues = (key: string, values: string[]): string => {
    if (key in rangeLabels) return formatRange(values);

    if (key === 'countryCode')
        return values.map(v => getCountryText(v) || v.toUpperCase()).join(', ');

    return values.map(v => formatValue(key, v)).join(', ');
};

const ReadOnlyFilterTag: React.FC<ReadOnlyFilterTagProps> = ({
    filterKey,
    values,
    onRemove,
    theme,
}) => {
    const label = filterLabels[filterKey] ?? filterKey;
    const displayValue = formatValues(filterKey, values);

    return (
        <SearchDateButton modalToggled={false} filterExists={true}>
            <SearchCriteriaButton
                filterExists={true}
                modalToggled={false}
                onClick={() => {}}
                type="button"
                data-testid={`${filterKey}-readonly-filter`}
                style={{ cursor: 'default' }}
            >
                <CriteriaText modalToggled={false} filterExists={true}>
                    <Filter>
                        {label}:{' '}
                        <FilterCriteria modalToggled={false} filterExists={true}>
                            {displayValue}
                        </FilterCriteria>
                    </Filter>
                </CriteriaText>
            </SearchCriteriaButton>
            <SearchClose
                data-testid={`${filterKey}-readonly-filter-remove`}
                modalToggled={false}
                onClick={() => onRemove(filterKey)}
            >
                <CloseLineIcon size={theme.icon.size.small} />
            </SearchClose>
        </SearchDateButton>
    );
};

export default withTheme(ReadOnlyFilterTag) as React.ComponentType<
    Omit<ReadOnlyFilterTagProps, 'theme'>
>;
